class PointsService {
  constructor() {
    this.key = 'ai_agent_points_v1';
    this.txKey = 'ai_agent_points_tx_v1';
    this.ratesKey = 'ai_agent_points_rates_v1';
    this.guestKey = 'ai_agent_guest_points_v1';
    this.guestTxKey = 'ai_agent_guest_tx_v1';
    this.userTypeKey = 'ai_agent_user_type_v1'; // 'guest' or 'logged_in'
    
    // 게스트 사용자 초기화
    this.initializeGuestUser();
    
    if (localStorage.getItem(this.key) === null) localStorage.setItem(this.key, '0');
    if (localStorage.getItem(this.txKey) === null) {
      localStorage.setItem(this.txKey, JSON.stringify([]));
    } else {
      // 과거에 너무 커진 경우를 대비해 크기 줄이기
      try {
        const arr = JSON.parse(localStorage.getItem(this.txKey) || '[]');
        if (Array.isArray(arr) && arr.length > 500) {
          localStorage.setItem(this.txKey, JSON.stringify(arr.slice(-200)));
        }
      } catch (_) {
        localStorage.setItem(this.txKey, JSON.stringify([]));
      }
    }
    if (localStorage.getItem(this.ratesKey) === null) {
      // 기본 요금표는 모델 기반으로 계산(고정 단가)
      localStorage.setItem(this.ratesKey, JSON.stringify({
        // write
        chatWrite: 300,
        reasonerWrite: 500,
        customWrite: 500,
        // edit
        chatEdit: 100,
        reasonerEdit: 200,
        customEdit: 200,
        // question
        chatQuestion: 100,
        reasonerQuestion: 200,
        customQuestion: 200,
        perKChars: 0                 // 추가 글자당 과금 비활성화
      }));
    }
  }

  // 게스트 사용자 초기화
  initializeGuestUser() {
    const userType = localStorage.getItem(this.userTypeKey);
    if (!userType) {
      // 처음 방문하는 사용자는 게스트로 설정하고 1500포인트 지급
      localStorage.setItem(this.userTypeKey, 'guest');
      localStorage.setItem(this.guestKey, '1500');
      localStorage.setItem(this.guestTxKey, JSON.stringify([
        { type: 'credit', amount: 1500, reason: '게스트 사용자 환영 포인트', timestamp: Date.now() }
      ]));
    }
  }

  // 사용자 타입 확인
  getUserType() {
    return localStorage.getItem(this.userTypeKey) || 'guest';
  }

  // 로그인 상태로 변경
  setLoggedIn() {
    localStorage.setItem(this.userTypeKey, 'logged_in');
  }

  // 로그아웃 상태로 변경
  setLoggedOut() {
    localStorage.setItem(this.userTypeKey, 'guest');
  }

  // 게스트 포인트 잔액 조회
  getGuestBalance() {
    return parseInt(localStorage.getItem(this.guestKey) || '0', 10);
  }

  // 게스트 포인트 설정
  setGuestBalance(value) {
    localStorage.setItem(this.guestKey, String(Math.max(0, Math.floor(value))));
  }

  // 게스트 포인트 추가
  addGuestPoints(amount, reason = '충전') {
    const newBal = this.getGuestBalance() + Math.max(0, Math.floor(amount));
    this.setGuestBalance(newBal);
    this._addGuestTx({ type: 'credit', amount, reason });
    return newBal;
  }

  // 게스트 포인트 차감
  deductGuestPoints(amount, reason = '사용') {
    const need = Math.max(0, Math.floor(amount));
    const bal = this.getGuestBalance();
    if (bal < need) return false;
    this.setGuestBalance(bal - need);
    this._addGuestTx({ type: 'debit', amount: need, reason });
    return true;
  }

  // 게스트 거래 내역 추가
  _addGuestTx(tx) {
    const arr = JSON.parse(localStorage.getItem(this.guestTxKey) || '[]');
    arr.unshift({ ...tx, timestamp: Date.now() });
    localStorage.setItem(this.guestTxKey, JSON.stringify(arr.slice(0, 100)));
  }

  // 게스트 거래 내역 조회
  getGuestTransactions(limit = 50) {
    const arr = JSON.parse(localStorage.getItem(this.guestTxKey) || '[]');
    return arr.slice(0, limit);
  }

  getBalance() {
    return parseInt(localStorage.getItem(this.key) || '0', 10);
  }

  setBalance(value) {
    localStorage.setItem(this.key, String(Math.max(0, Math.floor(value))));
  }

  // 포인트 설정 (외부에서 호출 가능)
  setPoints(value) {
    this.setBalance(value);
  }

  addPoints(amount, reason = '충전') {
    const newBal = this.getBalance() + Math.max(0, Math.floor(amount));
    this.setBalance(newBal);
    this._addTx({ type: 'credit', amount, reason });
    
    // Supabase에 포인트 동기화 (로그인 사용자만)
    this.syncPointsToSupabase();
    
    return newBal;
  }

  deductPoints(amount, reason = '사용') {
    const need = Math.max(0, Math.floor(amount));
    const bal = this.getBalance();
    if (bal < need) return false;
    
    const newBalance = bal - need;
    this.setBalance(newBalance);
    this._addTx({ type: 'debit', amount: need, reason });
    
    // Supabase에 포인트 동기화 (로그인 사용자만)
    this.syncPointsToSupabase();
    
    return true;
  }

  getTransactions(limit = 50) {
    const arr = JSON.parse(localStorage.getItem(this.txKey) || '[]');
    return arr.slice(-limit).reverse();
  }

  _addTx(tx) {
    // reason 길이 제한으로 저장 크기 축소
    const safeReason = (tx.reason || '').toString().slice(0, 200);
    const compactTx = { id: `tx_${Date.now()}`, time: new Date().toISOString(), ...tx, reason: safeReason };

    let arr;
    try {
      arr = JSON.parse(localStorage.getItem(this.txKey) || '[]');
      if (!Array.isArray(arr)) arr = [];
    } catch (_) {
      arr = [];
    }

    arr.push(compactTx);

    // 기본은 200개 유지
    let toSave = arr.slice(-200);
    try {
      localStorage.setItem(this.txKey, JSON.stringify(toSave));
    } catch (e1) {
      // 100개로 축소 후 재시도
      try {
        toSave = arr.slice(-100);
        localStorage.setItem(this.txKey, JSON.stringify(toSave));
      } catch (e2) {
        // 50개로 축소 후 재시도
        try {
          toSave = arr.slice(-50);
          localStorage.setItem(this.txKey, JSON.stringify(toSave));
        } catch (e3) {
          // 마지막으로 10개만 유지
          try {
            toSave = arr.slice(-10);
            localStorage.setItem(this.txKey, JSON.stringify(toSave));
          } catch (e4) {
            // 완전히 실패하면 비우기
            try { localStorage.setItem(this.txKey, JSON.stringify([])); } catch(_) {}
          }
        }
      }
    }
  }

  getRates() {
    return JSON.parse(localStorage.getItem(this.ratesKey) || '{}');
  }

  setRates(rates) {
    localStorage.setItem(this.ratesKey, JSON.stringify({ ...this.getRates(), ...rates }));
  }

  // 모델+모드 기반 단가 반환
  getRequestCost(model = 'oneline-chat', mode = 'write') {
    const r = this.getRates();
    const isReasoner = model === 'oneline-reasoner';
    const isCustom = model === 'oneline-custom';
    
    if (mode === 'edit') {
      if (isCustom) return r.customEdit ?? 200;
      return isReasoner ? (r.reasonerEdit ?? 200) : (r.chatEdit ?? 100);
    }
    if (mode === 'question') {
      if (isCustom) return r.customQuestion ?? 200;
      return isReasoner ? (r.reasonerQuestion ?? 200) : (r.chatQuestion ?? 100);
    }
    // write(default)
    if (isCustom) return r.customWrite ?? 500;
    return isReasoner ? (r.reasonerWrite ?? 500) : (r.chatWrite ?? 300);
  }

  // 스타터 포인트 제공/초기화
  resetAndGrantStarter(points = 1500) {
    this.setBalance(Math.max(0, Math.floor(points)));
    this._addTx({ type: 'credit', amount: points, reason: '신규 스타터 포인트' });
    return this.getBalance();
  }

  isLow(threshold = 50) {
    return this.getBalance() <= threshold;
  }

  // 통합 포인트 잔액 조회 (게스트/로그인 사용자 구분)
  getCurrentBalance() {
    const userType = this.getUserType();
    if (userType === 'guest') {
      return this.getGuestBalance();
    } else {
      return this.getBalance();
    }
  }

  // 통합 포인트 차감 (게스트/로그인 사용자 구분)
  deductCurrentPoints(amount, reason = '사용') {
    const userType = this.getUserType();
    if (userType === 'guest') {
      return this.deductGuestPoints(amount, reason);
    } else {
      const success = this.deductPoints(amount, reason);
      if (success) {
        // 로그인 사용자의 경우 Supabase에 동기화
        this.syncPointsToSupabase();
      }
      return success;
    }
  }

  // 통합 포인트 추가 (게스트/로그인 사용자 구분)
  addCurrentPoints(amount, reason = '충전') {
    const userType = this.getUserType();
    if (userType === 'guest') {
      return this.addGuestPoints(amount, reason);
    } else {
      const result = this.addPoints(amount, reason);
      // 로그인 사용자의 경우 Supabase에 동기화
      this.syncPointsToSupabase();
      return result;
    }
  }

  // 통합 거래 내역 조회 (게스트/로그인 사용자 구분)
  getCurrentTransactions(limit = 50) {
    const userType = this.getUserType();
    if (userType === 'guest') {
      return this.getGuestTransactions(limit);
    } else {
      return this.getTransactions(limit);
    }
  }

  // 통합 낮은 포인트 확인
  isCurrentLow(threshold = 50) {
    return this.getCurrentBalance() <= threshold;
  }

  // Supabase에 포인트 동기화
  async syncPointsToSupabase() {
    const userType = this.getUserType();
    if (userType === 'logged_in') {
      try {
        // Supabase 서비스 동적 import
        const supabaseService = (await import('./supabaseService')).default;
        const currentUser = supabaseService.getCurrentUser();
        
        if (currentUser && currentUser.email) {
          const currentBalance = this.getBalance();
          await supabaseService.updateUserPointsByEmail(currentUser.email, currentBalance);
        }
      } catch (error) {
        console.error('포인트 동기화 실패:', error);
      }
    }
  }
}

const pointsService = new PointsService();

// 전역에서 접근 가능하도록 설정
if (typeof window !== 'undefined') {
  window.pointsService = pointsService;
}

export default pointsService;


